import { useCallback, useEffect, useState } from "react";
import { Inbox, Search, ArrowRight, RefreshCcw, X } from "lucide-react";
import { http, notify } from "../api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function WebLeads({ user, onTriaged }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/web-leads", { params: q ? { search: q } : {} });
      setRows(r.data || []);
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Could not load web leads.");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const triage = async (lead_id) => {
    setBusy(lead_id);
    try {
      const r = await http.post(`/web-leads/${lead_id}/triage`);
      notify.success(
        r.data?.duplicate
          ? "Already in the Web Leads campaign — Data Assignment refreshed."
          : "Sent to Data Assignment. Distribute to a Sales Associate from the Campaigns screen."
      );
      setOpened(null);
      onTriaged && onTriaged();
      load();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Could not send to Data Assignment.");
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="web-leads-page">
      <div className="page-intro">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
          <Inbox size={20} /> Web Leads
        </h2>
        <p>
          Raw inquiries from <code>/website</code> and <code>/apply</code>. Click
          <strong> Send to Data Assignment </strong> to push a lead into the standard
          Campaigns / Calling-Data pipeline — from there, distribute it to a Sales
          Associate via the existing <strong>Campaigns</strong> screen. It then follows the
          normal SA workflow (Call → Disposition → Create Lead → Review → Send to Login).
        </p>
      </div>

      <div className="toolbar">
        <div className="table-search">
          <Search size={14} />
          <input
            data-testid="web-leads-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, mobile, or campaign"
          />
        </div>
        <button
          className="outline-button"
          data-testid="web-leads-refresh"
          onClick={load}
          title="Refresh"
        >
          <RefreshCcw size={14} /> Refresh
        </button>
        <span className="record-count" data-testid="web-leads-count">
          {loading ? "Loading…" : `${rows.length} inquir${rows.length === 1 ? "y" : "ies"}`}
        </span>
      </div>

      <div className="table-wrap" data-testid="web-leads-table">
        <table>
          <caption className="sr-only">Raw web leads awaiting triage</caption>
          <thead>
            <tr>
              <th>Received</th>
              <th>Name / lead id</th>
              <th>Mobile</th>
              <th>City</th>
              <th>Employment / income</th>
              <th>Loan request</th>
              <th>Source / campaign</th>
              <th style={{ textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.lead_id}
                data-testid={`web-lead-row-${r.lead_id}`}
                className="clickable-row"
                onClick={() => setOpened(r)}
              >
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{fmtDate(r.created_at)}</td>
                <td>
                  <strong>{r.customer_name}</strong>
                  <span className="cell-sub mono">{r.lead_id}</span>
                </td>
                <td className="mono">{r.mobile_masked}</td>
                <td>{r.city || <span className="muted">—</span>}</td>
                <td>
                  <span>{r.employment_type || "—"}</span>
                  <span className="cell-sub">{money(r.monthly_income)} /mo</span>
                </td>
                <td>
                  <span>{r.loan_type || "—"}</span>
                  <span className="cell-sub">{money(r.loan_amount)}</span>
                </td>
                <td>
                  <span>{r.source_channel}</span>
                  <span className="cell-sub">
                    {r.campaign_id ? `Camp ${r.campaign_id}` : <span className="muted">—</span>}
                  </span>
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`web-lead-triage-${r.lead_id}`}
                    className="primary-button compact"
                    disabled={busy === r.lead_id}
                    onClick={() => triage(r.lead_id)}
                  >
                    {busy === r.lead_id ? "Sending…" : <>Send to Data Assignment <ArrowRight size={13} /></>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="empty" data-testid="web-leads-empty">
            <Inbox size={28} style={{ opacity: 0.4 }} />
            <p>No raw web leads right now.</p>
            <small>New inquiries from <code>/website</code> or <code>/apply</code> will appear here.</small>
          </div>
        )}
      </div>

      {opened && (
        <WebLeadDetail
          row={opened}
          onClose={() => setOpened(null)}
          onTriage={() => triage(opened.lead_id)}
          busy={busy === opened.lead_id}
        />
      )}
    </div>
  );
}

function WebLeadDetail({ row, onClose, onTriage, busy }) {
  return (
    <div className="rx-modal-scrim" data-testid="web-lead-detail-modal" onClick={onClose}>
      <div className="rx-modal" onClick={(e) => e.stopPropagation()}>
        <header className="rx-modal-head">
          <div>
            <h3 style={{ margin: 0 }}>{row.customer_name}</h3>
            <small className="muted">
              <span className="mono">{row.lead_id}</span> · received {new Date(row.created_at).toLocaleString("en-IN")}
            </small>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="rx-modal-body">
          <dl className="rx-kv">
            <dt>Mobile</dt><dd className="mono">{row.mobile}</dd>
            <dt>City</dt><dd>{row.city || "—"}</dd>
            <dt>Employment</dt><dd>{row.employment_type || "—"}</dd>
            <dt>Monthly income</dt><dd>{money(row.monthly_income)}</dd>
            <dt>Required amount</dt><dd>{money(row.loan_amount)}</dd>
            <dt>Loan type</dt><dd>{row.loan_type || "—"}</dd>
            <dt>Source</dt><dd>{row.source_channel} · <span className="muted">{row.page}</span></dd>
            <dt>Campaign ID</dt><dd className="mono">{row.campaign_id || <span className="muted">—</span>}</dd>
            <dt>Ad ID</dt><dd className="mono">{row.ad_id || <span className="muted">—</span>}</dd>
            <dt>Ad Set ID</dt><dd className="mono">{row.adset_id || <span className="muted">—</span>}</dd>
            <dt>External Lead ID</dt><dd className="mono">{row.external_lead_id || <span className="muted">—</span>}</dd>
            {row.fbclid ? (
              <><dt>FBCLID</dt><dd className="mono" style={{ wordBreak: "break-all" }}>{row.fbclid}</dd></>
            ) : null}
          </dl>
        </div>

        <footer className="rx-modal-foot">
          <button className="outline-button" onClick={onClose}>Close</button>
          <button
            className="primary-button"
            disabled={busy}
            onClick={onTriage}
            data-testid="web-lead-modal-triage"
          >
            {busy ? "Sending…" : <>Send to Data Assignment <ArrowRight size={14} /></>}
          </button>
        </footer>
      </div>
    </div>
  );
}
